import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';

import { trackEvent } from '../eventService';
import { getClientLocation, geolocate } from '../geolocationUtils';
import OnboardingPrompt from './OnboardingPrompt';
import { decodeQueryString } from '../queryStringUtils';

const ONBOARDING_EXPIRES_AT = 'onboardingExpiresAt';

const IS_RECENTLY_ONBOARDED = (() => {
  let onboardingExpiresAt;
  try {
    onboardingExpiresAt = window.localStorage.getItem(ONBOARDING_EXPIRES_AT);
  } catch (e) {
    onboardingExpiresAt = null;
  }
  const isPreviouslyOnboarded = onboardingExpiresAt !== null;
  if (isPreviouslyOnboarded) {
    const isExpired = Number(onboardingExpiresAt) < Date.now();
    if (isExpired) {
      try {
        window.localStorage.removeItem(ONBOARDING_EXPIRES_AT);
      } catch (e) {
        // do nothing
      }
      return false;
    }
    return true;
  }
  return false;
})();

const setOnboardingExpiresAt = () => {
  const durationInDays = 30;
  const expiresAt = Date.now() + durationInDays * 24 * 3600 * 1000;
  try {
    window.localStorage.setItem(ONBOARDING_EXPIRES_AT, expiresAt);
  } catch (e) {
    // do nothing
  }
};

const useOnBoarding = (setParentState) => {
  const { pathname, search } = useLocation();
  const clientLocation = getClientLocation();
  const queryObject = decodeQueryString(search);
  const isSearchParams = queryObject.searchType && queryObject.searchString;

  const [isOnboarded, setIsOnboarded] = useState(IS_RECENTLY_ONBOARDED || clientLocation !== null || pathname !== '/' || isSearchParams);
  const onboardWithLocation = () => {
    geolocate()
      .then(([longitude, latitude]) => {
        setParentState({ center: [longitude, latitude], zoom: [14] });
      })
      .catch(() => {
        // eslint-disable-next-line no-alert
        alert('We were unable to get your location. Make sure that your browser supports geolocation and that this website is allowed to get your location.');
      })
      .finally(() => {
        setIsOnboarded(true);
        trackEvent('map', 'interaction', 'geo-target');
      });
  };
  const onboard = () => {
    setIsOnboarded(true);
    setOnboardingExpiresAt();
    trackEvent('map', 'interaction', 'noshare');
  };
  return [isOnboarded, onboardWithLocation, onboard];
};

const OnboardingBoundry = ({ children, setParentState }) => {
  const [isOnboarded, onboardWithLocation, onboard] = useOnBoarding(setParentState);

  if (isOnboarded) {
    return children;
  }

  return (
    <OnboardingPrompt
      onWithLocationClick={onboardWithLocation}
      onWithoutLocationClick={onboard}
    />
  );
};

OnboardingBoundry.propTypes = {
  children: PropTypes.node.isRequired,
  setParentState: PropTypes.func.isRequired,
};

export default OnboardingBoundry;
